import { DayOfWeek } from "@redotech/crdb-enums/inventory/day-of-week";
import type { SupportUserAvailabilityOverride } from "@redotech/crdb-enums/support-v2/support-user-availability-override";
import type { WidgetHours } from "@redotech/redo-customer-widget-common/widget-hours";
import * as z from "zod";

/** Structurally identical to team business hours so `isWithinHours` fits both. */
export type WeeklySchedule = WidgetHours;

/**
 * Day keys in JS `getDay()` order, so Sunday sits at index 0 and a `getDay()`
 * or `dayOfWeek % 7` result indexes straight into it. `satisfies` pins the
 * generated enum's values to the schedule's own keys.
 */
export const WEEKDAY_KEYS = [
  DayOfWeek.Sunday,
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
] as const satisfies readonly (keyof WeeklySchedule)[];

const timeBlockSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
});

const dayScheduleSchema = z.object({
  enabled: z.boolean().default(false),
  timeBlocks: z.array(timeBlockSchema).default([]),
});

const weeklyScheduleSchema_ = z.object({
  sunday: dayScheduleSchema.optional(),
  monday: dayScheduleSchema.optional(),
  tuesday: dayScheduleSchema.optional(),
  wednesday: dayScheduleSchema.optional(),
  thursday: dayScheduleSchema.optional(),
  friday: dayScheduleSchema.optional(),
  saturday: dayScheduleSchema.optional(),
});

/** Annotated so the schema cannot drift from `WidgetHours` silently. */
export const weeklyScheduleSchema: z.ZodType<WeeklySchedule> =
  weeklyScheduleSchema_;

/** One date whose hours replace that weekday's weekly hours entirely. */
export const scheduleOverrideSchema = z.object({
  id: z.string(),
  /** `YYYY-MM-DD` local date in the schedule's timezone. */
  date: z.string(),
  /** When false the whole day is off and `timeBlocks` is ignored. */
  available: z.boolean(),
  timeBlocks: z.array(timeBlockSchema).default([]),
  note: z.string().optional(),
});

export type ScheduleOverride = z.infer<typeof scheduleOverrideSchema>;

export const scheduleOverridesSchema = z.array(scheduleOverrideSchema);

/** Which schedule layer decided the on-/off-shift baseline. */
export enum WorkScheduleReason {
  /** No schedule configured — the user is always assignable. */
  NO_SCHEDULE = "no_schedule",
  /** Manual available/offline toggle, in effect until it expires. */
  MANUAL_OVERRIDE = "manual_override",
  /** A date-specific override governs today. */
  SCHEDULE_OVERRIDE = "schedule_override",
  WITHIN_HOURS = "within_hours",
  OUTSIDE_HOURS = "outside_hours",
}

/** Derived, never stored: system-observed, so it stays out of the override enum. */
export enum WorkScheduleAvailability {
  AVAILABLE = "available",
  /** On a call: unassignable now, but still on shift and back shortly. */
  BUSY = "busy",
  /** Off shift, on a day off, or manually offline. */
  OFFLINE = "offline",
}

/** Live voice-side state describing now; omit it for a past instant. */
export interface LivePresence {
  readonly onCall: boolean;
}

/** The persisted schedule, as the evaluator consumes it. */
export interface WorkSchedule {
  readonly timezone: string;
  readonly scheduleEnabled: boolean;
  readonly weeklyHours: WeeklySchedule;
  readonly scheduleOverrides: readonly ScheduleOverride[];
  /** Null means every channel. */
  readonly assignableChannels: readonly string[] | null;
  readonly overrideStatus: SupportUserAvailabilityOverride | null;
  readonly overrideExpiresAt: Date | null;
}

export interface WorkScheduleStatus {
  /** Assignable right now, i.e. `availability` is `AVAILABLE`. False while busy. */
  readonly active: boolean;
  /** Tri-state for display. Busy never masks an offline user. */
  readonly availability: WorkScheduleAvailability;
  /** The schedule layer that decided the baseline; unaffected by busy. */
  readonly reason: WorkScheduleReason;
  /** Next derived (pre-override) flip. Always strictly in the future. */
  readonly nextTransitionAt: Date;
  /** Whether that flip puts the user on shift, i.e. a shift starting not ending. */
  readonly nextTransitionActive: boolean;
}
